import type { GroupMeetingPrepBrief, MeetingPrepBrief } from 'types/meetingPrep';
import { isCanonicalNumericRouteId } from 'utils/linkUtil';
import { isGroupBrief } from './meetingPrepBriefGuards';

const MEETING_PREP_INTERNAL_HREF_BASE = 'https://meeting-prep.internal';
const MEETING_PREP_RECORD_ROUTE_SEGMENTS = new Set([
    'contact',
    'employee',
    'group',
    'matter',
    'invoice',
    'effort',
    'task',
]);

/**
 * The contact-detail tabs a capped table can hand the reader on to. `efforts` and `referrals` are
 * BD-dropdown child tabs, not tabs of their own, but `/contact/:id/:tab` still matches them.
 */
export type MeetingPrepClientTab = 'cases' | 'invoices' | 'efforts' | 'referrals' | 'tasks';

const MEETING_PREP_CLIENT_TAB_SEGMENTS = new Set<string>([
    'cases',
    'invoices',
    'efforts',
    'referrals',
    'tasks',
] satisfies MeetingPrepClientTab[]);

/** The group-detail tabs a capped table can hand the reader on to. `GroupPage`'s `general` tab lists no capped table's rows, so it's left out. */
export type MeetingPrepGroupTab = 'cases' | 'referrals' | 'efforts';

const MEETING_PREP_GROUP_TAB_SEGMENTS = new Set<string>([
    'cases',
    'referrals',
    'efforts',
] satisfies MeetingPrepGroupTab[]);

/** The tabs of `recordSegment` a capped-table note may point at, or null when it owns none. */
function tabSegmentsFor(recordSegment: string): Set<string> | null {
    if (recordSegment === 'contact') {
        return MEETING_PREP_CLIENT_TAB_SEGMENTS;
    }

    return recordSegment === 'group' ? MEETING_PREP_GROUP_TAB_SEGMENTS : null;
}

/**
 * The portal path a brief's href points at, or null when it isn't one this page will route to. Two
 * forms pass: a record page (`/matter/789`), or an allow-listed detail tab (`/contact/42/cases`).
 */
export function resolveMeetingPrepInternalHref(href: string): string | null {
    let parsedUrl: URL;

    try {
        parsedUrl = new URL(href, MEETING_PREP_INTERNAL_HREF_BASE);
    } catch {
        return null;
    }

    if (parsedUrl.origin !== MEETING_PREP_INTERNAL_HREF_BASE) {
        return null;
    }

    const segments = parsedUrl.pathname.split('/').filter(Boolean);

    if (segments.length === 3) {
        const [recordSegment, id, tabSegment] = segments;
        const allowedTabSegments = tabSegmentsFor(recordSegment);

        if (allowedTabSegments === null || !allowedTabSegments.has(tabSegment)) {
            return null;
        }

        return isCanonicalNumericRouteId(id) ? `/${recordSegment}/${id}/${tabSegment}` : null;
    }

    if (segments.length !== 2) {
        return null;
    }

    const [recordSegment, id] = segments;

    if (!MEETING_PREP_RECORD_ROUTE_SEGMENTS.has(recordSegment)) {
        return null;
    }

    if (!isCanonicalNumericRouteId(id)) {
        return null;
    }

    return `/${recordSegment}/${id}`;
}

/** `recordUrl` with `tab` appended, once confirmed to be the bare detail page of `recordSegment`. */
function resolveRecordTabHref(
    recordUrl: string | null | undefined,
    recordSegment: 'contact' | 'group',
    tab: string
): string | null {
    const recordPath = recordUrl ? resolveMeetingPrepInternalHref(recordUrl) : null;

    if (recordPath === null) {
        return null;
    }

    const segments = recordPath.split('/').filter(Boolean);

    if (segments.length !== 2 || segments[0] !== recordSegment) {
        return null;
    }

    return `${recordPath}/${tab}`;
}

/** Derived from the client's own href so no extra field has to reach the payload. */
export function resolveMeetingPrepClientTabHref(
    clientUrl: string | null | undefined,
    tab: MeetingPrepClientTab
): string | null {
    return resolveRecordTabHref(clientUrl, 'contact', tab);
}

/** Derived from the group's own href, the same way as `resolveMeetingPrepClientTabHref`. */
export function resolveMeetingPrepGroupTabHref(
    groupUrl: string | null | undefined,
    tab: MeetingPrepGroupTab
): string | null {
    return resolveRecordTabHref(groupUrl, 'group', tab);
}

function isGroupTab(tab: MeetingPrepClientTab): tab is MeetingPrepGroupTab {
    return MEETING_PREP_GROUP_TAB_SEGMENTS.has(tab);
}

/** Null for tabs the group page doesn't own (invoices, BD tasks); the section then shows no note. */
export function resolveMeetingPrepBriefTabHref(
    brief: MeetingPrepBrief | GroupMeetingPrepBrief,
    tab: MeetingPrepClientTab
): string | null {
    if (!isGroupBrief(brief)) {
        return resolveMeetingPrepClientTabHref(brief.clientUrl, tab);
    }

    return isGroupTab(tab) ? resolveMeetingPrepGroupTabHref(brief.groupUrl, tab) : null;
}
